import dayjs from 'dayjs';
import { CareTeamItem } from '../../types/CareTeam';
import { CustomPatient, PatientInfo, PatientListItem } from '../../types/PatientRequest';
import { CoCMStatusType } from '../../types/PatientType';
import { RescorePatient } from '../../types/RescoreInterviewJob';
import { TimeTrackerAssessmentItem } from '../../types/TimeTracker';
import { Provider } from '../../types/UserTypes';

export const getPatientName = (
  patient: PatientInfo | PatientListItem | RescorePatient | CustomPatient
) => {
  const patientId = (patient as PatientListItem).id || (patient as PatientInfo).patientId;
  if (patient.lastName && patient.firstName) {
    return `${patient.lastName}, ${patient.firstName} (${patientId})`;
  }
  if (patient.lastName || patient.firstName || patient.externalId) {
    return `${patient.lastName || patient.firstName || patient.externalId} (${patientId})`;
  }
  return patientId;
};

export const getPatientFullNameForDropwDown = (
  patient: PatientInfo | PatientListItem | RescorePatient | CustomPatient
) => {
  const patientId = (patient as PatientListItem).id || (patient as PatientInfo).patientId;
  let patientName = '';
  if (!!patient.externalId && !patient.lastName && !patient.firstName) {
    patientName = `${patient.externalId} (${patientId})`;
  } else if (!patient.lastName && !patient.firstName) {
    patientName = ` (${patientId})`;
  } else if (!patient.lastName && patient.firstName) {
    patientName = `${patient.firstName} (${patientId})`;
  } else if (patient.lastName && !patient.firstName) {
    patientName = `${patient.lastName} (${patientId})`;
  } else {
    patientName = `${patient.lastName}, ${patient.firstName} (${patientId})`;
  }
  return patientName;
};

export const getPatientFullName = (patient: RescorePatient) => {
  if (patient.firstName && patient.lastName) {
    return `${patient.lastName}, ${patient.firstName}`;
  }
  if (patient.firstName || patient.lastName) return `${patient.lastName || patient.firstName}`;
  return '';
};

export const CoCMStatus: CoCMStatusType[] = [
  { id: 1, code: 'E', name: 'Enrolled' },
  { id: 2, code: 'ES', name: 'Enrolled & Screened' },
  { id: 3, code: 'EA', name: 'Enrolled & Assessed' },
  { id: 4, code: 'T', name: 'Treatment' },
  { id: 5, code: 'RPP', name: 'Relapse Prevention Plan' },
  { id: 6, code: 'D', name: 'Deactivate' },
];

export const getSortedUsers = <T extends Provider | CareTeamItem>(users: T[]): T[] => {
  return [...users].sort(
    (a, b) =>
      (a.lastName || '').localeCompare(b.lastName || '') ||
      (a.firstName || '').localeCompare(b.firstName || '') ||
      0
  );
};

export const getSortedCareTeam = (careTeam: CareTeamItem[]) => {
  return getSortedUsers(careTeam || []);
};

export const getCareTeamOptionLabel = (option: CareTeamItem) => {
  return option?.firstName && option?.lastName
    ? `${option.lastName}, ${option.firstName} (${option.roleName})`
    : '';
};

export const getCareTeamValue = (option: CareTeamItem) => {
  return `${option.id}-${option.type}`;
};

export const getTimeTrackerAssessmentsOptionLabel = (option: TimeTrackerAssessmentItem) => {
  return `${option.name} (${dayjs(option.completedAt).format('MM/DD/YYYY')})`;
};

export const getTimeTrackerAssessmentValue = (option: TimeTrackerAssessmentItem) => {
  return `${option.id}-${option.type}`;
};

export const getCurrentMonthTimeTracker = () => {
  const monthMap = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];

  const currentDate = new Date();
  const formattedMonth = monthMap[currentDate.getMonth()];
  return formattedMonth;
};

export const getNumberInUSFormat = (number: number) => {
  if (!number) return 0;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
  return formatted;
};
